import React from 'react';
import styled from 'styled-components';
import breakpoints from '@/constants/breakpoints';
import cards from './cards-data';
import ImageAnnotation from './ImageAnnotation/ImageAnnotation';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';

import 'swiper/swiper-bundle.min.css';
import useMedia from '@/hooks/useMedia';
import RoundedArrow from '../common/RoundedArrow';

const baseImgUrl = 'https://theinside.imgix.net/homepage/rebrand';

export default function ShopTheLook() {
  return (
    <Container>
      <Header>
        <Title>Shop the Look</Title>
      </Header>
      <Images cards={cards} />
    </Container>
  );
}

const Images = ({ cards }) => {
  const isMobile = useMedia(`(max-width: ${breakpoints['tablet'] - 1}px)`);

  const carouselBreakpoints = {
    [breakpoints['tablet']]: {
      slidesPerView: 3.71,
      slidesOffsetBefore: 21
    },
    [breakpoints['desktop']]: {
      slidesPerView: 3,
      spaceBetween: 20,
      slidesOffsetBefore: 0
    },
    [breakpoints['large-desktop']]: {
      slidesPerView: 4,
      slidesOffsetBefore: 0
    }
  };

  if (isMobile)
    return (
      <MobileCardsWrapper>
        <MobileCards cards={cards} />
      </MobileCardsWrapper>
    );

  return (
    <DesktopCards>
      <Swiper
        spaceBetween={10}
        slidesPerView={0}
        simulateTouch={false}
        className="slider"
        navigation={{
          nextEl: `${NextButton}`,
          prevEl: `${PrevButton}`
        }}
        breakpoints={carouselBreakpoints}
        modules={[Navigation]}
      >
        {cards.map(({ id, component, image, annotations }) => (
          <SwiperSlide className="slide" key={id}>
            {component ? (
              component
            ) : (
              <ImageContainer>
                <ImageAnnotation
                  imageId={id}
                  imageAsset={`${baseImgUrl}/${image}`}
                  annotations={annotations}
                />
              </ImageContainer>
            )}
          </SwiperSlide>
        ))}
      </Swiper>

      <NavButtons>
        <PrevButton>
          <RoundedArrow direction="left" />
        </PrevButton>
        <NextButton>
          <RoundedArrow />
        </NextButton>
      </NavButtons>
    </DesktopCards>
  );
};

const MobileCards = ({ cards }) => {
  return cards.map(({ id, component, image, annotations }) => (
    <div key={id}>
      {component ? (
        component
      ) : (
        <ImageContainer>
          <div className="image-wrapper">
            <ImageAnnotation
              imageAsset={`${baseImgUrl}/${image}`}
              annotations={annotations}
            />
          </div>
        </ImageContainer>
      )}
    </div>
  ));
};

const Container = styled.div`
  padding: 38px 0 0;
  margin: 0 auto;
  max-width: 1620px;

  @media (min-width: ${breakpoints['tablet']}px) {
    width: 100%;
    padding: 47px 0 0;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    width: 82%;
    padding: 56px 0 0;
  }

  @media (min-width: ${breakpoints['large-desktop']}px) {
    padding: 64px 0 0;
  }

  .slider {
    width: 100%;
  }

  .slide {
    @media (min-width: ${breakpoints['tablet']}px) {
      height: 100%;
    }
  }
`;

const MobileCardsWrapper = styled.div`
  display: grid;
  margin: 0 auto 44.5px;
  grid-template-columns: 1fr 1fr;
  grid-gap: 10px;
  max-width: 343px;

  @media (min-width: ${breakpoints['small-tablet']}px) {
    grid-template-columns: 1fr 1fr 1fr;
    max-width: 550px;
  }
`;

const DesktopCards = styled.div`
  position: relative;
  display: flex;
  margin-bottom: 44px;

  @media (min-width: ${breakpoints['desktop']}px) {
    margin-bottom: 74px;
  }
`;

const Header = styled.div`
  max-width: 343px;
  margin: 0 auto 18.5px;

  @media (min-width: ${breakpoints['small-tablet']}px) {
    max-width: 550px;
  }

  @media (min-width: ${breakpoints['tablet']}px) {
    margin-left: 21px;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    max-width: unset;
    margin-left: 0;
    margin-bottom: 24.6px;
  }
`

const Title = styled.h2`
  text-align: left;
  font-family: ${({ theme }) => theme.fonts.secondary.family};
  line-height: normal;
  font-size: 24px;
  font-weight: 300;
  letter-spacing: -0.48px;
  color: ${props => props.theme.colors.charcoal};  

  @media (min-width: ${breakpoints['tablet']}px) {
    text-align: left;
    font-size: 28px;
    letter-spacing: -0.56px;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    font-size: 38px;
    letter-spacing: -0.76px;
  }

  @media (min-width: ${breakpoints['large-desktop']}px) {
    font-size: 45px;
    letter-spacing: -1.5px;
  }
`;

const ImageContainer = styled.div`
  width: 100%;

  .image-wrapper {
    top: 0;
    left: 0;
    height: inherit;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .image {
    position: absolute;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const NavButtons = styled.div`
  display: none;
  position: absolute;
  top: 50%;
  width: 100%;

  @media (min-width: ${breakpoints['desktop']}px) {
    display: block;
  }

  a {
    margin: 0;
    cursor: pointer;
    position: absolute;
    z-index: 1;
  }

  a:nth-child(1) {
    left: -75px;
  }
  a:nth-child(2) {
    right: -75px;
  }

  .swiper-button-disabled {
    display: none;
  }
`;

const PrevButton = styled.a``;
const NextButton = styled.a``;
